<script setup lang="ts">
/**
 * CompanionVisual — L3 da Proposta Lume, Fase 5.
 *
 * Ilustração minimalista do Companion. SVG inline, sem mascotes
 * manipulativos, sem dependência de CDN.
 *
 * Constitucional:
 * - Princípio X (Dignidade): zero "olhinhos" / mascote infantil.
 * - P-UX-10 (Quiet as feature): visual calmo, paleta da marca.
 * - Princípio XI (Companion ≠ LLM): não é um avatar de chatbot.
 */
import { computed } from "vue";
import type { CompanionVisualId } from "../types";

interface Props {
  visual: CompanionVisualId;
  size?: number;
  ariaLabel?: string;
}

const props = withDefaults(defineProps<Props>(), {
  size: 64,
  ariaLabel: "Companion",
});

const paths = computed(() => {
  switch (props.visual) {
    case "minimal-1":
      return {
        shape: "circle",
        accent: "M32 12 v40 M12 32 h40",
      };
    case "minimal-2":
      return {
        shape: "rounded-square",
        accent: "M20 20 L44 44 M44 20 L20 44",
      };
    case "minimal-3":
      return {
        shape: "diamond",
        accent: "M32 16 L48 32 L32 48 L16 32 Z",
      };
    case "none":
    default:
      return null;
  }
});
</script>

<template>
  <svg
    v-if="paths"
    :width="size"
    :height="size"
    :viewBox="'0 0 64 64'"
    role="img"
    :aria-label="ariaLabel"
    class="hlos-companion-visual"
    data-testid="companion-visual"
  >
    <!-- Fundo sutil -->
    <circle cx="32" cy="32" r="30" fill="var(--hlos-color-neutral-100)" />
    <!-- Forma principal -->
    <circle
      v-if="paths.shape === 'circle'"
      cx="32"
      cy="32"
      r="16"
      fill="var(--hlos-color-primary)"
      opacity="0.85"
    />
    <rect
      v-else-if="paths.shape === 'rounded-square'"
      x="18"
      y="18"
      width="28"
      height="28"
      rx="6"
      fill="var(--hlos-color-primary)"
      opacity="0.85"
    />
    <polygon
      v-else-if="paths.shape === 'diamond'"
      points="32,16 48,32 32,48 16,32"
      fill="var(--hlos-color-primary)"
      opacity="0.85"
    />
    <!-- Acento (sutil, "sopro"/"vitalidade" sem manipular) -->
    <path
      :d="paths.accent"
      stroke="var(--hlos-color-bg)"
      stroke-width="2"
      stroke-linecap="round"
      fill="none"
    />
  </svg>
  <span v-else class="hlos-companion-visual__placeholder" aria-hidden="true" />
</template>

<style scoped>
.hlos-companion-visual {
  display: inline-block;
  vertical-align: middle;
}
.hlos-companion-visual__placeholder {
  display: inline-block;
  width: 1px;
  height: 1px;
}
</style>
